import {useMemo} from "react";
import {DiagnosticRow} from "./DiagnosticRow";
import {SEVERITY_ORDER} from "./types";
import type {Diagnostic, DiagnosticSeverity, ObjectRef} from "./types";

export type DiagnosticListProps = {
  diagnostics?: Diagnostic[];
  canNavigate?: (ref: ObjectRef) => boolean;
  onNavigate?: (ref: ObjectRef) => void;
  emptyText?: string;
};

const groupLabel: Record<DiagnosticSeverity, string> = {
  CRITICAL: "Critical",
  ERROR: "Error",
  WARNING: "Warning",
  INFO: "Info"
};

/**
 * A run's findings, worst first.
 *
 * Takes the base `Diagnostic` shape rather than a module type, so report diagnostics and any future
 * module's diagnostics render through this one component.
 */
export const DiagnosticList = ({
  diagnostics,
  canNavigate,
  onNavigate,
  emptyText = "No warnings or errors."
}: DiagnosticListProps) => {
  const grouped = useMemo(() => {
    return SEVERITY_ORDER
      .map((severity) => ({
        severity,
        // An unrecognized severity falls into WARNING, matching severityRank, so a code this client
        // does not know still gets shown rather than silently dropped.
        items: (diagnostics ?? []).filter((diagnostic) => {
          const value = diagnostic.core?.severity;
          return value === severity
            || (severity === "WARNING" && !SEVERITY_ORDER.includes(value as DiagnosticSeverity));
        })
      }))
      .filter((group) => group.items.length > 0);
  }, [diagnostics]);

  if (!diagnostics?.length) {
    return <div className="p-6 text-center text-sm text-brand-600 dark:text-brand-300">{emptyText}</div>;
  }

  return (
    <div className="flex flex-col gap-2">
      {grouped.map((group) => (
        <div key={group.severity} className="flex flex-col gap-2">
          <div className="mt-1 text-xs font-bold uppercase tracking-wide text-brand-700 dark:text-brand-300">
            {groupLabel[group.severity]}
            {group.items.length > 1 ? ` (${group.items.length})` : ""}
          </div>
          {group.items.map((diagnostic, index) => (
            <DiagnosticRow
              key={diagnostic.core?.id ?? `${group.severity}-${index}`}
              diagnostic={diagnostic}
              canNavigate={canNavigate}
              onNavigate={onNavigate}/>
          ))}
        </div>
      ))}
    </div>
  );
};
